import { supabase } from '@/lib/supabase';

export type ChangePasswordResult =
  | { success: true }
  | { success: false; error: string };

function mapAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return 'Current password is incorrect.';
  }
  if (lower.includes('same password')) {
    return 'New password must be different from your current password.';
  }
  return message;
}

/**
 * Re-authenticate with the current password, then update to the new password.
 */
export async function changePassword(params: {
  email: string;
  currentPassword: string;
  newPassword: string;
}): Promise<ChangePasswordResult> {
  const email = params.email.trim();
  if (!email) {
    return { success: false, error: 'No email address found for your account.' };
  }

  const { error: reauthError } = await supabase.auth.signInWithPassword({
    email,
    password: params.currentPassword,
  });

  if (reauthError) {
    return { success: false, error: mapAuthError(reauthError.message) };
  }

  const { error: updateError } = await supabase.auth.updateUser({
    password: params.newPassword,
  });

  if (updateError) {
    return { success: false, error: mapAuthError(updateError.message) };
  }

  return { success: true };
}

export function validateChangePasswordInput(params: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  minLength?: number;
}): string | null {
  const minLength = params.minLength ?? 8;

  if (!params.currentPassword) {
    return 'Please enter your current password.';
  }
  if (!params.newPassword) {
    return 'Please enter a new password.';
  }
  if (params.newPassword.length < minLength) {
    return `New password must be at least ${minLength} characters.`;
  }
  if (params.newPassword !== params.confirmPassword) {
    return 'New password and confirmation do not match.';
  }
  if (params.currentPassword === params.newPassword) {
    return 'New password must be different from your current password.';
  }
  return null;
}
